import React, { useMemo, useState } from "react";
import CopyableContainer from "./CopyableContainer";
import LoadingRow from "./LoadingRow";
import "./styles/tableStyles.css";
import "./styles/positionBreakdown.css";

const DISPLAY_CATEGORIES = ["QB", "RB", "WR", "TE", "D/ST", "K"];
const SORT_KEYS = {
    OWNER: "owner",
    TOTAL: "total_points",
};

const formatPoints = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "0.0";
    return numeric.toFixed(1);
};

const formatShare = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "0%";
    return `${Math.round(numeric * 100)}%`;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const buildCategoryStats = (teams, categories) =>
    categories.reduce((acc, category) => {
        const values = teams
            .map((team) => Number(team.breakdown?.[category]))
            .filter((value) => Number.isFinite(value));

        const min = values.length ? Math.min(...values) : 0;
        const max = values.length ? Math.max(...values) : 0;
        acc[category] = { min, max };
        return acc;
    }, {});

const getHeatStyle = (value, share, stats) => {
    const numericValue = Number(value) || 0;
    const numericShare = Number(share) || 0;
    const min = Number(stats?.min) || 0;
    const max = Number(stats?.max) || 0;
    const valueRange = max - min;
    const valueIntensity =
        valueRange <= 0 ? 0.5 : clamp((numericValue - min) / valueRange, 0, 1);
    const shareIntensity = clamp(numericShare / 0.5, 0, 1);
    const intensity = Math.pow(valueIntensity * 0.78 + shareIntensity * 0.22, 0.9);

    return {
        backgroundColor: `hsl(43 78% ${96 - intensity * 21}%)`,
        borderColor: `hsl(43 73% ${82 - intensity * 24}%)`,
        boxShadow: `inset 0 0 0 1px hsla(43, 70%, ${78 - intensity * 28}%, 0.35)`,
    };
};

const sortTeamsDescending = (teams, sortKey, showPercentages = false) =>
    [...teams].sort((left, right) => {
        if (sortKey === SORT_KEYS.OWNER) {
            return String(right.owner || right.team || "").localeCompare(
                String(left.owner || left.team || "")
            );
        }

        const leftValue = DISPLAY_CATEGORIES.includes(sortKey)
            ? Number(
                  (showPercentages ? left.shares?.[sortKey] : left.breakdown?.[sortKey]) || 0
              )
            : Number(left?.[sortKey] || 0);
        const rightValue = DISPLAY_CATEGORIES.includes(sortKey)
            ? Number(
                  (showPercentages ? right.shares?.[sortKey] : right.breakdown?.[sortKey]) || 0
              )
            : Number(right?.[sortKey] || 0);

        if (rightValue !== leftValue) {
            return rightValue - leftValue;
        }

        return String(left.owner || left.team || "").localeCompare(
            String(right.owner || right.team || "")
        );
    });

const SeasonMatrixTable = ({ data, week, loading }) => {
    const [sortKey, setSortKey] = useState(SORT_KEYS.TOTAL);
    const [showPercentages, setShowPercentages] = useState(false);
    const rawCategories = data?.categories || DISPLAY_CATEGORIES;
    const categories = useMemo(
        () =>
            DISPLAY_CATEGORIES.filter((category) =>
                rawCategories.includes(category)
            ),
        [rawCategories]
    );
    const teams = useMemo(
        () => (Array.isArray(data?.teams) ? data.teams : []),
        [data?.teams]
    );
    const sortedTeams = useMemo(
        () => sortTeamsDescending(teams, sortKey, showPercentages),
        [teams, sortKey, showPercentages]
    );
    const categoryStats = buildCategoryStats(teams, categories);

    const renderSortableHeader = (label, key) => {
        const isActive = sortKey === key;
        return (
            <button
                type="button"
                className={`position-matrix-sort ${isActive ? "position-matrix-sort--active" : ""}`}
                onClick={() => setSortKey(key)}
            >
                <span>{label}</span>
                <span className="position-matrix-sort-indicator" aria-hidden="true">
                    ↓
                </span>
            </button>
        );
    };

    return (
        <CopyableContainer
            fileName={`season-matrix-through-week-${week}`}
            className="position-breakdown-container position-breakdown-container--matrix"
        >
            <div className="position-breakdown-shell">
                <div className="position-breakdown-heading">
                    <div>
                        <p className="position-breakdown-eyebrow">Season Matrix</p>
                        <h3>Season-to-date through Week {week}</h3>
                    </div>
                    <button
                        type="button"
                        className={`position-matrix-display-toggle ${showPercentages ? "position-matrix-display-toggle--active" : ""}`}
                        role="switch"
                        aria-checked={showPercentages}
                        aria-label="Show percentages"
                        onClick={() => setShowPercentages((current) => !current)}
                    >
                        <span className="position-matrix-display-toggle-track" aria-hidden="true">
                            <span className="position-matrix-display-toggle-thumb" />
                        </span>
                        <span>{showPercentages ? "Percentages" : "Total points"}</span>
                    </button>
                </div>

                <div className="table-rounded-shell">
                    <table className="table position-matrix-table">
                        <thead>
                            <tr>
                                <th>{renderSortableHeader("Owner", SORT_KEYS.OWNER)}</th>
                                {categories.map((category) => (
                                    <th key={category}>{renderSortableHeader(category, category)}</th>
                                ))}
                                <th>{renderSortableHeader("Total", SORT_KEYS.TOTAL)}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading || !data ? (
                                <LoadingRow text="Loading Season Matrix..." colSpan={categories.length + 2} />
                            ) : teams.length === 0 ? (
                                <tr>
                                    <td colSpan={categories.length + 2} style={{ textAlign: "center" }}>
                                        No season matrix data available.
                                    </td>
                                </tr>
                            ) : (
                                sortedTeams.map((team, index) => (
                                    <tr key={team.team_id || `${team.owner}-${team.team}`} className={index % 2 === 0 ? "even-row" : "odd-row"}>
                                        <td className="team-name-column position-matrix-team">
                                            <strong>{team.owner || team.team}</strong>
                                            {team.owner && team.team ? (
                                                <span>{team.team}</span>
                                            ) : null}
                                        </td>
                                        {categories.map((category) => (
                                            <td key={category} className="position-matrix-value-cell">
                                                <div
                                                    className="position-matrix-heat"
                                                    style={getHeatStyle(
                                                        team.breakdown?.[category],
                                                        team.shares?.[category],
                                                        categoryStats[category]
                                                    )}
                                                >
                                                    <strong>
                                                        {showPercentages
                                                            ? formatShare(team.shares?.[category])
                                                            : formatPoints(team.breakdown?.[category])}
                                                    </strong>
                                                </div>
                                            </td>
                                        ))}
                                        <td className="position-matrix-total">{formatPoints(team.total_points)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </CopyableContainer>
    );
};

export default SeasonMatrixTable;
